import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";

const homeFor = (role) => (role === "super_admin" || role === "admin" ? "/dashboard" : "/projects");

export default function LoginPage() {
  const { login, register, isAuthed, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname;

  const [mode, setMode] = useState("login"); // 'login' | 'register'
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [busy, setBusy] = useState(false);

  // Already signed in → bounce into the app.
  useEffect(() => {
    if (isAuthed && user) navigate(from || homeFor(user.role), { replace: true });
  }, [isAuthed, user, from, navigate]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    setBusy(true);
    try {
      const data =
        mode === "login"
          ? await login({ email: form.email, password: form.password })
          : await register(form);
      navigate(from || homeFor(data.user.role), { replace: true });
    } catch (err) {
      const res = err.response?.data;
      setError(res?.message || err.message || "Something went wrong");
      setFieldErrors(res?.errors || {});
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="proj-auth-wrap">
      <div className="proj-auth-card">
        <div className="proj-auth-head">
          <span className="logo-mark" />
          <h1>AdIntel AI</h1>
          <p className="proj-muted">
            {mode === "login" ? "Sign in to manage your projects" : "Create your account"}
          </p>
        </div>

        <form onSubmit={submit} className="proj-form">
          {mode === "register" && (
            <label className="proj-field">
              <span>Name</span>
              <input value={form.name} onChange={set("name")} placeholder="Your name" autoFocus />
              {fieldErrors.name && <em className="proj-err">{fieldErrors.name[0]}</em>}
            </label>
          )}
          <label className="proj-field">
            <span>Email</span>
            <input
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder="you@example.com"
              autoComplete="username"
            />
            {fieldErrors.email && <em className="proj-err">{fieldErrors.email[0]}</em>}
          </label>
          <label className="proj-field">
            <span>Password</span>
            <input
              type="password"
              value={form.password}
              onChange={set("password")}
              placeholder="••••••••"
              autoComplete={mode === "login" ? "current-password" : "new-password"}
            />
            {fieldErrors.password && <em className="proj-err">{fieldErrors.password[0]}</em>}
          </label>

          {error && <div className="proj-alert">{error}</div>}

          <button className="proj-btn proj-btn-primary" disabled={busy} type="submit">
            {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
          </button>
        </form>

        <div className="proj-auth-foot proj-muted">
          {mode === "login" ? (
            <>
              No account?{" "}
              <button className="proj-link" onClick={() => setMode("register")}>
                Create one
              </button>
            </>
          ) : (
            <>
              Already have an account?{" "}
              <button className="proj-link" onClick={() => setMode("login")}>
                Sign in
              </button>
            </>
          )}
          {mode === "register" && (
            <p className="proj-hint">
              Password needs 8+ chars with an uppercase, lowercase, number and special character.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
